import React from "react";
import { Button, OneLineList } from "../../../components/ui";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import { ClientDetail } from "../types/clients.types";

/** Label followed by its value, the repeating unit of every card here. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline gap-2">
    <span className="flex-shrink-0 text-[13px] 3xl:text-[14px] text-ink-muted">
      {label}:
    </span>
    {/* `min-w-0` so a value that measures itself against this cell — the
        allergy line below — is given the cell's width rather than its own
        content's, which would never overflow and so never count anything. */}
    <span className="min-w-0 text-[14px] 3xl:text-[15px] font-medium text-ink">
      {value}
    </span>
  </div>
);

// The one-line list the Goal and Food allergy fields below are drawn with used
// to live here. It is `components/ui/OneLineList` now — the enquiry screen
// shows its own goals the same way, and two copies of a measuring pass is two
// places for it to drift.

/**
 * A number with its dialling code. The code stays in the label's grey so the
 * value still reads as just the number — and it is rendered rather than stored,
 * the same way the forms draw it beside the field.
 */
const DiallingCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="font-normal text-ink-muted">+91</span> {children}
  </>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="rounded-md border border-line bg-white">{children}</section>
);

/**
 * One row of fields inside a card.
 *
 * Three even columns rather than `justify-between`: the fields spread across
 * the card the way the design has them, without the last one being pushed
 * flush against the right edge. A second row sets `divided`, which is what
 * draws the rule between it and the row above.
 */
const Row: React.FC<{ children: React.ReactNode; divided?: boolean }> = ({
  children,
  divided = false,
}) => (
  <div
    className={
      "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 px-5 sm:px-6 py-4 " +
      (divided ? "border-t border-line" : "")
    }
  >
    {children}
  </div>
);

interface ClientDetailsTabProps {
  client: ClientDetail;
  /**
   * Whether this workspace may still change anything, read once by the screen
   * above — see `ClientDetailPage`. The record stays readable either way; it is
   * the one button that writes which goes off.
   */
  gate: SubscriptionGate;
  /** Opens the edit form, filled with this client's own answers. */
  onEdit: () => void;
}

/**
 * Everything on file about one client, card by card.
 *
 * The cards after the address are mostly the optional half of the record —
 * what they weigh, how they eat, how their day runs. The two that hold nothing
 * a client is required to have, the dishes and the day, are left off entirely
 * where nothing is on record, the way the Plan card at the foot already is:
 * a card of dashes says less than no card. The body and food cards are drawn
 * either way, since the goal and the food preference on them are answers every
 * client has.
 *
 * All of it fills in as the pre-consultation form comes back, or as somebody
 * types it into the edit screen.
 */
const ClientDetailsTab: React.FC<ClientDetailsTabProps> = ({
  client,
  gate,
  onEdit,
}) => (
  <div className="flex flex-col gap-4">
    <Card>
      <div className="flex flex-wrap items-center gap-3 px-5 sm:px-6 py-5">
        <span className="w-11 h-11 rounded-full bg-[#DEEBFB] flex items-center justify-center flex-shrink-0">
          <span className="text-[16px] font-medium text-[#2F6BAE]">
            {client.name.charAt(0)}
          </span>
        </span>
        <h2 className="text-[18px] sm:text-[20px] font-medium text-ink">
          {client.salutation} {client.name}
        </h2>
        <span className="inline-flex rounded-full border border-[#A6EDF5] bg-[#F8FEFF] px-2.5 py-0.5 text-[12px] font-medium text-[#00899B]">
          {client.status}
        </span>

        {/* Pushed to the end of the row so it reads as an action on the header
            rather than another badge — the same place the enquiry screen puts
            its own. On this tab alone, since it is these fields it edits: the
            one action that belongs to the whole client rather than to this tab
            — sending the pre-consultation form — is up in the page header, and
            stands there whichever tab is open. */}
        <Button
          size="sm"
          className="ml-auto"
          onClick={onEdit}
          {...gate.props("edit a client")}
        >
          Edit details
        </Button>
      </div>

      {/* Spread edge to edge so the last field lines up with the card's right
          padding, the way the design has it. */}
      <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 border-t border-line px-5 sm:px-6 py-4">
        <Field label="Gender" value={client.gender} />
        <Field label="DOB" value={client.dob} />
        <Field label="Age" value={client.age ?? "-"} />
        <Field label="Life style" value={client.lifestyle} />
        <Field label="Profession" value={client.profession} />
      </div>
    </Card>

    <Card>
      <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 px-5 sm:px-6 py-4">
        <Field label="Phone" value={<DiallingCode>{client.phone}</DiallingCode>} />
        <Field
          label="WhatsApp"
          value={<DiallingCode>{client.whatsapp}</DiallingCode>}
        />
        <Field label="Email" value={client.email} />
      </div>
      <div className="border-t border-line px-5 sm:px-6 py-4">
        <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
          Address:
        </div>
        <div className="mt-1 text-[14px] 3xl:text-[15px] font-medium text-ink">
          {client.address}
        </div>
      </div>
    </Card>

    {/* What they weigh and what they are aiming for, with the goal that gives
        the target its meaning on the same line — a target weight beside a goal
        of "Weight gain" reads differently from the same figure beside "Weight
        loss", and the design puts the two together for that reason. */}
    <Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 px-5 sm:px-6 py-4">
        <Field label="Height" value={client.body.height} />
        <Field label="Weight" value={client.body.weight} />
        <Field label="Target weight" value={client.body.targetWeight} />
        <Field
          label="Goal"
          value={<OneLineList items={client.goals} label="goals" />}
        />
      </div>
    </Card>

    <Card>
      <Row>
        <Field label="Food preference" value={client.foodPreference} />
        <Field label="Favorite cuisines" value={client.food.cuisine} />
        <Field
          label="Food allergy"
          value={
            <OneLineList items={client.foodAllergies} label="food allergies" />
          }
        />
      </Row>
      {/* What they are being treated for sits under what they eat rather than
          in a card of its own: the two are read together when a plan is being
          written, and a condition on a separate card is one a practitioner
          scrolls past. */}
      <Row divided>
        <Field label="Conditions" value={client.food.conditions} />
        <Field label="Medications" value={client.food.medications} />
      </Row>
    </Card>

    {/* Only where they said something. Two empty lists is a card that asks a
        question rather than answers one. */}
    {client.food.anyDishes && (
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 px-5 sm:px-6 py-4">
          <Field
            label="Dishes you love"
            value={
              <OneLineList
                items={client.food.dishesLoved}
                label="dishes they love"
              />
            }
          />
          <Field
            label="Dishes you avoid"
            value={
              <OneLineList
                items={client.food.dishesAvoided}
                label="dishes they avoid"
              />
            }
          />
        </div>
      </Card>
    )}

    {client.day.any && (
      <Card>
        <Row>
          <Field label="Travel frequency" value={client.day.travel} />
          <Field label="Alcohol consumption" value={client.day.alcohol} />
          <Field label="Smoking" value={client.day.smoking} />
        </Row>
        <Row divided>
          <Field label="Working hours" value={client.day.workingHours} />
          <Field label="Sleep timings" value={client.day.sleepTimings} />
        </Row>
      </Card>
    )}

    {/* What they bought, from the payment request the record carries. Left off
        entirely for a client who holds no subscription: a card of three dashes
        says less than no card, and the Payments tab is where an empty history
        is spoken to. */}
    {client.subscription && (
      <Card>
        <Row>
          <Field label="Plan" value={client.subscription.plan} />
          <Field label="Paid price" value={client.subscription.paidPrice} />
          <Field label="Purchase date" value={client.subscription.purchaseDate} />
        </Row>
      </Card>
    )}
  </div>
);

export default ClientDetailsTab;
